import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  FormControlLabel,
  Checkbox,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Repeat as RepeatIcon,
  Search as DetectIcon,
  CalendarMonth as CalendarIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { MainLayout } from '../components/MainLayout';
import { EmptyState } from '../components/EmptyState';
import { RecurringTransactionsService } from '../services/recurring-transactions-service';
import type {
  RecurringTransaction,
  RecurringFrequency,
} from '../types/api';

const FREQUENCY_LABELS: Record<RecurringFrequency, string> = {
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
};

const FREQUENCY_OPTIONS: RecurringFrequency[] = [
  'weekly',
  'biweekly',
  'monthly',
  'quarterly',
  'yearly',
];

/**
 * Compute the monthly equivalent cost for a recurring transaction.
 *
 * Args:
 *     amount (number): The transaction amount.
 *     frequency (RecurringFrequency): How often the transaction recurs.
 *
 * Returns:
 *     number: The estimated monthly cost.
 */
function toMonthlyCost(amount: number, frequency: RecurringFrequency): number {
  switch (frequency) {
    case 'weekly':
      return amount * (52 / 12);
    case 'biweekly':
      return amount * (26 / 12);
    case 'monthly':
      return amount;
    case 'quarterly':
      return amount / 3;
    case 'yearly':
      return amount / 12;
    default:
      return amount;
  }
}

/**
 * Calculate the number of days until a given date string.
 *
 * Args:
 *     dateStr (string): ISO date string.
 *
 * Returns:
 *     number: Days from today (negative if in the past).
 */
function daysUntil(dateStr: string): number {
  const target = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Get a color for the due date chip based on urgency.
 *
 * Args:
 *     days (number): Number of days until due.
 *
 * Returns:
 *     The MUI chip color variant.
 */
function getDueDateColor(days: number): 'error' | 'warning' | 'success' | 'default' {
  if (days < 0) return 'error';
  if (days <= 3) return 'error';
  if (days <= 7) return 'warning';
  return 'default';
}

export const RecurringTransactionsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<RecurringTransaction[]>([]);
  const [upcoming, setUpcoming] = useState<RecurringTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  // Create/Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RecurringTransaction | null>(null);
  const [formDescription, setFormDescription] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formFrequency, setFormFrequency] = useState<RecurringFrequency>('monthly');
  const [formNextDueDate, setFormNextDueDate] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Detect patterns state
  const [detecting, setDetecting] = useState(false);
  const [detectedPatterns, setDetectedPatterns] = useState<RecurringTransaction[]>([]);
  const [detectDialogOpen, setDetectDialogOpen] = useState(false);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await RecurringTransactionsService.list(!showInactive);
      setTransactions(res.recurring_transactions || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load recurring transactions');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  const fetchUpcoming = useCallback(async () => {
    try {
      const res = await RecurringTransactionsService.getUpcoming();
      setUpcoming(res.upcoming || []);
    } catch {
      // Reason: Upcoming is supplementary data; don't block the page if it fails
      setUpcoming([]);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
    fetchUpcoming();
  }, [fetchTransactions, fetchUpcoming]);

  const openCreateDialog = () => {
    setEditingItem(null);
    setFormDescription('');
    setFormAmount('');
    setFormFrequency('monthly');
    setFormNextDueDate(new Date().toISOString().split('T')[0]);
    setFormNotes('');
    setDialogOpen(true);
  };

  const openEditDialog = (item: RecurringTransaction) => {
    setEditingItem(item);
    setFormDescription(item.description);
    setFormAmount(item.amount);
    setFormFrequency(item.frequency as RecurringFrequency);
    setFormNextDueDate(item.next_due_date);
    setFormNotes(item.notes || '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingItem) {
        await RecurringTransactionsService.update(editingItem.id, {
          description: formDescription,
          amount: formAmount,
          frequency: formFrequency,
          next_due_date: formNextDueDate,
          // Reason: auto_create is no longer user-editable (see the UI removal note
          // near the dialog form below), but the column still stores real values.
          // Echo the existing value back so editing a row does not silently wipe it.
          auto_create: editingItem.auto_create,
          notes: formNotes || undefined,
        });
      } else {
        await RecurringTransactionsService.create({
          description: formDescription,
          amount: formAmount,
          frequency: formFrequency,
          next_due_date: formNextDueDate,
          // Reason: no UI control exists for this any more; new rows default to
          // off, matching the DB default, until materialization is implemented.
          auto_create: false,
          notes: formNotes || undefined,
        });
      }
      setDialogOpen(false);
      fetchTransactions();
      fetchUpcoming();
    } catch (err: any) {
      setError(err.message || 'Failed to save recurring transaction');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this recurring transaction? This cannot be undone.')) return;
    try {
      await RecurringTransactionsService.delete(id);
      fetchTransactions();
      fetchUpcoming();
    } catch (err: any) {
      setError(err.message || 'Failed to delete recurring transaction');
    }
  };

  const handleToggleActive = async (item: RecurringTransaction) => {
    try {
      await RecurringTransactionsService.update(item.id, {
        description: item.description,
        amount: item.amount,
        frequency: item.frequency as RecurringFrequency,
        next_due_date: item.next_due_date,
        auto_create: item.auto_create,
        notes: item.notes || undefined,
      });
      fetchTransactions();
      fetchUpcoming();
    } catch (err: any) {
      setError(err.message || 'Failed to update transaction status');
    }
  };

  const handleDetectPatterns = async () => {
    setDetecting(true);
    try {
      const res = await RecurringTransactionsService.detectPatterns();
      setDetectedPatterns(res.detected || []);
      setDetectDialogOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to detect patterns');
    } finally {
      setDetecting(false);
    }
  };

  // Reason: Calculate total monthly cost from all active transactions for the summary header
  const totalMonthlyCost = transactions
    .filter((t) => t.is_active)
    .reduce((sum, t) => {
      return sum + toMonthlyCost(parseFloat(t.amount), t.frequency as RecurringFrequency);
    }, 0);

  return (
    <MainLayout>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Recurring Transactions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage subscriptions, bills, and recurring payments
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2">Show inactive</Typography>}
            />
            <Button
              variant="outlined"
              startIcon={detecting ? <CircularProgress size={16} /> : <DetectIcon />}
              onClick={handleDetectPatterns}
              disabled={detecting}
            >
              Detect Patterns
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
              New Recurring
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Monthly Cost Summary */}
        {!loading && transactions.length > 0 && (
          <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <MoneyIcon sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Estimated Monthly Cost
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    ${totalMonthlyCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Active Subscriptions
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {transactions.filter((t) => t.is_active).length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Bills (next 30 days) */}
        {!loading && upcoming.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon fontSize="small" />
              Upcoming Bills (Next 30 Days)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {upcoming.map((item) => {
                const days = daysUntil(item.next_due_date);
                const chipColor = getDueDateColor(days);
                return (
                  <Card key={`upcoming-${item.id}`} variant="outlined" sx={{ minWidth: 220, flex: '0 1 auto' }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {item.description}
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            ${parseFloat(item.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                        <Chip
                          label={
                            days === 0
                              ? 'Today'
                              : days < 0
                                ? `${Math.abs(days)}d overdue`
                                : `${days}d`
                          }
                          color={chipColor}
                          size="small"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Transaction List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : transactions.length === 0 ? (
          <EmptyState
            icon={<RepeatIcon />}
            title="No recurring transactions yet"
            description="Add your subscriptions and recurring bills to track upcoming payments and estimate monthly costs."
            actionLabel="Add Recurring Transaction"
            onAction={openCreateDialog}
          />
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
              gap: 3,
            }}
          >
            {transactions.map((item) => {
              const amount = parseFloat(item.amount);
              const days = daysUntil(item.next_due_date);
              const monthly = toMonthlyCost(amount, item.frequency as RecurringFrequency);

              return (
                <Card
                  key={item.id}
                  sx={{
                    position: 'relative',
                    opacity: item.is_active ? 1 : 0.6,
                    borderLeft: `4px solid`,
                    borderColor: item.is_active ? 'primary.main' : 'text.disabled',
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="h6" fontWeight={600}>
                            {item.description}
                          </Typography>
                          {!item.is_active && (
                            <Chip label="Inactive" size="small" color="default" />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={FREQUENCY_LABELS[item.frequency as RecurringFrequency] || item.frequency}
                            size="small"
                            variant="outlined"
                          />
                          {/*
                            Reason: an "Auto-create" badge used to render here from
                            item.auto_create. It was removed because nothing in the
                            backend ever acts on that flag — there is no scheduler,
                            cron or ticker that materializes a recurring transaction
                            or advances next_due_date/last_occurrence, so the badge
                            advertised behaviour the product does not have. The
                            column and API field are intentionally retained; restore
                            this badge only once materialization actually ships.
                          */}
                        </Box>
                      </Box>
                      <Box>
                        <IconButton size="small" onClick={() => openEditDialog(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(item.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Amount */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h5" fontWeight="bold">
                        ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ~${monthly.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/month
                      </Typography>
                    </Box>

                    {/* Next Due Date */}
                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Next due:
                      </Typography>
                      <Chip
                        label={
                          days === 0
                            ? 'Today'
                            : days < 0
                              ? `${Math.abs(days)} days overdue`
                              : `${new Date(item.next_due_date + 'T00:00:00').toLocaleDateString()} (${days}d)`
                        }
                        size="small"
                        color={getDueDateColor(days)}
                      />
                    </Box>

                    {item.last_occurrence && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Last: {new Date(item.last_occurrence + 'T00:00:00').toLocaleDateString()}
                      </Typography>
                    )}

                    {item.notes && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1, fontStyle: 'italic' }}
                      >
                        {item.notes}
                      </Typography>
                    )}

                    {/* Active toggle */}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={item.is_active}
                            onChange={() => handleToggleActive(item)}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="caption">
                            {item.is_active ? 'Active' : 'Inactive'}
                          </Typography>
                        }
                      />
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingItem ? 'Edit Recurring Transaction' : 'New Recurring Transaction'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                fullWidth
                required
                placeholder="e.g., Netflix, Rent, Gym Membership"
              />
              <TextField
                label="Amount"
                value={formAmount}
                onChange={(e) => setFormAmount(e.target.value)}
                type="number"
                fullWidth
                required
              />
              <FormControl fullWidth required>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={formFrequency}
                  label="Frequency"
                  onChange={(e) => setFormFrequency(e.target.value as RecurringFrequency)}
                >
                  {FREQUENCY_OPTIONS.map((freq) => (
                    <MenuItem key={freq} value={freq}>
                      {FREQUENCY_LABELS[freq]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Next Due Date"
                value={formNextDueDate}
                onChange={(e) => setFormNextDueDate(e.target.value)}
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
              {/*
                Reason: the "Automatically create transaction when due" checkbox was
                deliberately removed. recurring_transactions.auto_create is stored and
                round-trips through the API, but NO backend code ever reads it to act
                on it: there is no scheduler/cron/ticker that materializes a due
                recurring transaction, and nothing advances next_due_date or
                last_occurrence. The control therefore promised a behaviour that does
                not exist. The DB column, domain field and API DTO are intentionally
                kept so existing values survive. Re-add this control only when
                materialization is actually implemented — see the Vitest test that
                asserts its absence before "restoring" it.
              */}
              <TextField
                label="Notes"
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                multiline
                rows={2}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !formDescription || !formAmount || !formNextDueDate}
            >
              {saving ? <CircularProgress size={20} /> : editingItem ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Detected Patterns Dialog */}
        <Dialog
          open={detectDialogOpen}
          onClose={() => setDetectDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Detected Recurring Patterns</DialogTitle>
          <DialogContent>
            {detectedPatterns.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No recurring patterns detected from your transaction history.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                {detectedPatterns.map((pattern, idx) => (
                  <Card key={idx} variant="outlined">
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Typography variant="body1" fontWeight={600}>
                            {pattern.description}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ${parseFloat(pattern.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} -{' '}
                            {FREQUENCY_LABELS[pattern.frequency as RecurringFrequency] || pattern.frequency}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setDetectDialogOpen(false);
                            setEditingItem(null);
                            setFormDescription(pattern.description);
                            setFormAmount(pattern.amount);
                            setFormFrequency(pattern.frequency as RecurringFrequency);
                            setFormNextDueDate(
                              pattern.next_due_date ||
                                new Date().toISOString().split('T')[0]
                            );
                            setFormNotes('');
                            setDialogOpen(true);
                          }}
                        >
                          Add
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetectDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};
